// Only mutually exclusive findings share a group. Coexisting findings stay intact.
const exclusiveGroups = {
  airway: [['partially_blocked', 'blocked']],
  breathing: [
    ['increased_rate', 'decreased_rate'],
    ['dyspnea_mild', 'dyspnea_moderate', 'dyspnea_severe'],
    ['vesicular_left', 'vesicular_right']
  ],
  circulation: [['tachycardia', 'bradycardia'], ['hypertension', 'hypotension']],
  disability: [['hypoglycemia', 'hyperglycemia']],
  ekg: [['sinus_rhythm', 'atrial_fibrillation', 'pacemaker'], ['stemi', 'nstemi']],
  allergies: [['known', 'none', 'unknown']],
  medications: [['as_prescribed', 'none', 'irregular', 'unknown']],
  initial: [['in_bed', 'on_chair']]
};

export function toggleFinding(selected = [], id, category) {
  if (selected.includes(id)) return selected.filter(item => item !== id);
  if (category === 'history' || category === 'riskFactors') {
    return id === 'none' ? ['none'] : [...selected.filter(item => item !== 'none'), id];
  }
  const group = (exclusiveGroups[category] || []).find(items => items.includes(id)) || [];
  return [...selected.filter(item => !group.includes(item)), id];
}

export function normalizeFindings(selected = [], category) {
  const unique = [...new Set(selected)];
  // Specific findings take priority over legacy "none" selections.
  const findings = ['history', 'riskFactors'].includes(category) && unique.length > 1
    ? unique.filter(item => item !== 'none') : unique;
  return findings.reduce((result, id) => toggleFinding(result, id, category), []);
}

export const hasValue = value => value !== undefined && value !== null && String(value).trim() !== '';
export const numericValue = value => hasValue(value) ? Number(String(value).replace(',', '.')) : undefined;

export function inputValue(data, input) {
  if (data?.[input.id] !== undefined) return data[input.id];
  const suppressFor = {
    respiratory_rate: ['increased_rate', 'decreased_rate', 'hyperventilation'],
    heart_rate: ['tachycardia', 'bradycardia'],
    bp_systolic: ['hypertension', 'hypotension'],
    bp_diastolic: ['hypertension', 'hypotension'],
    gcs: ['unconscious', 'confusion', 'seizure_active'],
    blood_sugar: ['hypoglycemia', 'hyperglycemia'],
    spo2: ['cyanosis_yes']
  };
  if ((data?.abnormalities || []).some(id => (suppressFor[input.id] || []).includes(id))) return '';
  if (input.id === 'gcs' && data?.orientations && data.orientations.length < 4) return '';
  return input.default ?? '';
}

export function getReportContext(form) {
  const abcde = form.abcde || {};
  const sampler = form.sampler || {};
  const symptoms = sampler.symptoms || [];
  const events = sampler.event || [];
  const breathing = abcde.breathing?.abnormalities || [];
  const circulation = abcde.circulation?.abnormalities || [];
  const disability = abcde.disability?.abnormalities || [];
  const exposure = abcde.exposure?.abnormalities || [];
  const stu = form.stu?.enabled ? form.stu.findings || [] : [];
  const measures = form.measures?.measures || [];
  return {
    dyspnea: symptoms.includes('dyspnea') || events.includes('dyspnea'),
    chestPain: symptoms.includes('chest_pain') || events.includes('chest_pain'),
    pain: symptoms.some(id => ['pain', 'chest_pain'].includes(id)) || events.includes('chest_pain') ||
      circulation.includes('chest_pain') || stu.some(id => ['neck_pain', 'chest_injury', 'abdomen_pain', 'spine_pain'].includes(id)),
    nausea: symptoms.includes('nausea'),
    confusion: symptoms.includes('confusion') || disability.includes('confusion'),
    seizure: events.includes('seizure'),
    symptomDetails: Boolean(sampler.symptoms_freetext?.trim()),
    injuries: stu.some(id => ['head_injury', 'chest_injury', 'pelvis_unstable', 'extremities_injury'].includes(id)) ||
      exposure.some(id => ['deformity', 'bruising'].includes(id)) || measures.some(id => ['wound_care', 'fracture_splint'].includes(id)),
    chestInjury: stu.includes('chest_injury'),
    cyanosis: circulation.includes('skin_cyanotic') || (abcde.airway?.abnormalities || []).includes('mucosa_cyanotic'),
    anyCyanosis: breathing.includes('cyanosis_yes') || circulation.includes('skin_cyanotic') ||
      (abcde.airway?.abnormalities || []).includes('mucosa_cyanotic'),
    oxygen: measures.some(id => ['oxygen_mask', 'oxygen_nasal'].includes(id)),
    impaired: disability.some(id => ['unconscious', 'seizure_active'].includes(id)) || numericValue(abcde.disability?.gcs) < 15,
    orientationIncomplete: Array.isArray(abcde.disability?.orientations) && abcde.disability.orientations.length < 4,
    airwaySupport: (abcde.airway?.abnormalities || []).some(id => ['blocked', 'intubated'].includes(id)),
    stu
  };
}